import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Mail, Phone, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// SmartCaddy Logo Component - Fresh Theme
const CaddyLogo = ({ size = 'default' }) => {
  const sizes = {
    small: { icon: 'w-8 h-8', text: 'text-xl' },
    default: { icon: 'w-10 h-10', text: 'text-2xl' },
  };
  const s = sizes[size];
  
  return (
    <div className="flex items-center gap-2">
      <div className={`${s.icon} rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/30`}>
        <ShoppingCart className="w-1/2 h-1/2 text-white" />
      </div>
      <span className={`font-caddy ${s.text} lowercase text-teal-600`}>caddy</span>
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cart');
  const { sendOtp, verifyOtp, isAuthenticated } = useAuth();

  const [step, setStep] = useState('contact'); // contact | otp
  const [contactType, setContactType] = useState('email');
  const [contact, setContact] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [mockOtp, setMockOtp] = useState(null);

  useEffect(() => {
    if (isAuthenticated && cartId) {
      navigate(`/shop?cart=${cartId}`);
    } else if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, cartId, navigate]);

  const handleSendOtp = async () => {
    if (!contact.trim()) {
      toast.error(`Please enter your ${contactType === 'email' ? 'email' : 'phone number'}`);
      return;
    }

    if (contactType === 'email' && !contact.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const result = await sendOtp(contact, contactType);
      setStep('otp');
      
      if (result.test_mode || result.mock || result.otp) {
        setMockOtp(result.otp);
        toast.success(`OTP sent to your ${contactType}`);
      } else {
        toast.success(`OTP sent to your ${contactType}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await verifyOtp(contact, otp, cartId);
      toast.success('Login successful!');
      
      if (cartId) {
        navigate(`/shop?cart=${cartId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative px-4 py-4 flex items-center gap-4 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <Button
          data-testid="back-btn"
          variant="ghost"
          size="icon"
          className="text-slate-600 hover:bg-slate-100"
          onClick={() => step === 'otp' ? setStep('contact') : navigate('/')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <CaddyLogo size="small" />
      </div>

      {/* Main Content */}
      <div className="relative flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-xl shadow-slate-200/50">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-['Outfit'] text-slate-800">
                {step === 'contact' ? 'Welcome to Caddy' : 'Enter OTP'}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {step === 'contact' 
                  ? 'Sign in to start your smart shopping experience'
                  : `We've sent a code to ${contact}`
                }
              </CardDescription>
            </CardHeader>

            <CardContent>
              {step === 'contact' ? (
                <div className="space-y-6">
                  <Tabs value={contactType} onValueChange={setContactType}>
                    <TabsList className="grid w-full grid-cols-2 bg-slate-100">
                      <TabsTrigger 
                        value="email" 
                        data-testid="email-tab"
                        className="data-[state=active]:bg-teal-500 data-[state=active]:text-white"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </TabsTrigger>
                      <TabsTrigger 
                        value="whatsapp" 
                        data-testid="whatsapp-tab"
                        className="data-[state=active]:bg-teal-500 data-[state=active]:text-white"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        WhatsApp
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="email" className="mt-4">
                      <Input
                        data-testid="email-input"
                        type="email"
                        placeholder="Enter your email"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                        className="h-12 bg-slate-50 border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </TabsContent>

                    <TabsContent value="whatsapp" className="mt-4">
                      <Input
                        data-testid="phone-input"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                        className="h-12 bg-slate-50 border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </TabsContent>
                  </Tabs>

                  <Button
                    data-testid="send-otp-btn"
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-teal-500/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <InputOTP
                      data-testid="otp-input"
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                    >
                      <InputOTPGroup className="gap-2">
                        {[0,1,2,3,4,5].map((index) => (
                          <InputOTPSlot 
                            key={index}
                            index={index} 
                            className="w-12 h-14 bg-slate-50 border-slate-300 text-slate-800 text-xl rounded-lg"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  {mockOtp && (
                    <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                      <p className="text-sm text-slate-500 mb-1">Test mode OTP</p>
                      <p className="text-teal-600 font-mono text-2xl tracking-widest">{mockOtp}</p>
                    </div>
                  )}

                  <Button
                    data-testid="verify-otp-btn"
                    onClick={handleVerifyOtp}
                    disabled={loading || otp.length !== 6}
                    className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-teal-500/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Login'
                    )}
                  </Button>

                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full text-center text-sm text-slate-500 hover:text-teal-600"
                  >
                    Didn&apos;t receive the code? Resend
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {cartId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-6 text-center"
            >
              <p className="text-slate-500 text-sm">
                Signing in for caddy <span className="text-teal-600 font-mono">#{cartId.slice(0, 8)}</span>
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
